'use client';

import {
  type JSX,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { setUserId } from 'firebase/analytics';
import { Auth, User, getAuth, onAuthStateChanged } from 'firebase/auth';

import { BootStatus } from '../components/core/BootStatus';
import { useFirebase } from './FirebaseContext';

export type AuthProps = {
  auth: Auth;
  user: User | null;
};

export const AuthContext = createContext<AuthProps | null>(null);

export const AuthContextProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [auth, setAuth] = useState<Auth>();
  const [user, setUser] = useState<User | null>();
  const [failed, setFailed] = useState(false);
  const { firebaseApp, analytics } = useFirebase();

  useEffect(() => {
    const auth = getAuth(firebaseApp);

    setAuth(auth);

    if (analytics !== undefined) {
      setUserId(analytics, auth.currentUser?.uid || null);
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        setFailed(false);
        setUser(user ?? null);
      },
      // Without this the observer's failures — a token refresh that cannot reach
      // Firebase, a disabled account, a clock too far out for the SDK — are
      // swallowed and `user` simply stays undefined, which this component renders
      // as the splash and never leaves.
      (error) => {
        console.error('Firebase rejected the auth state observer.', error);
        setFailed(true);
      },
    );

    return () => unsubscribe();
  }, [analytics, firebaseApp]);

  if (failed || auth === undefined || user === undefined) {
    return (
      <BootStatus
        failed={failed}
        message="No hemos podido verificar tu sesión. Comprueba tu conexión e inténtalo de nuevo."
        // Reloading rather than resubscribing: the observer's failures are the SDK's
        // own state, and there is nothing here to re-run that would clear it.
        onRetry={() => {
          window.location.reload();
        }}
        retryLabel="Recargar"
      />
    );
  }

  return (
    <AuthContext.Provider value={{ auth, user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within a AuthContextProvider');
  }

  return context;
};
